import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { trackEvent, EVENTS } from '../utils/analytics';
import { AuthContext } from '../context/AuthContext';
import config from '../config/config';
import MetricsCards from './MetricsCards';
import PortfolioPieChart from './PortfolioPieChart';
import StockCard from './StockCard';
import PortfolioStockHeader from './PortfolioStockHeader';
import ChartToggle from './ChartToggle';
import PerformanceChart from './PerformanceChart';
import PortfolioGrowthChart from './PortfolioGrowthChart';
import PortfolioUpdates from './PortfolioUpdates';
import { CURRENCY_SYMBOLS, formatNumber, formatCurrencyValue } from '../utils/formatters';
import { sortStocks } from '../utils/sorting';
import '../styles/Watchlist.css';


const Portfolio = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const addStockParam = searchParams.get('addStock');
    const { token, getAuthHeaders } = useContext(AuthContext);

    // Core data state
    const [portfolio, setPortfolio] = useState([]);
    const [loading, setLoading] = useState(false);

    // UI state
    const [activeOverviewTab, setActiveOverviewTab] = useState('metrics');
    const [sortBy, setSortBy] = useState('total_growth');
    const [sortOrder, setSortOrder] = useState('desc');
    const [filteredStocks, setFilteredStocks] = useState(null);
    const [activeChartView, setActiveChartView] = useState('value');
    const [activeDistributionView, setActiveDistributionView] = useState('stocks');
    const [selectedRange, setSelectedRange] = useState('year');
    const [isOverviewExpanded, setIsOverviewExpanded] = useState(true);
    const [showEmptyGuide, setShowEmptyGuide] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

    // Performance data state
    const [performanceData, setPerformanceData] = useState({
        values: [],
        growth: [],
        dividends: [],
        summary: {
            averageGrowth: 0,
            totalGrowth: 0,
            periodGrowth: 0,
            currency: 'USD'
        }
    });

    // Settings state
    const [preferredCurrency, setPreferredCurrency] = useState('USD');
    const availableCurrencies = ['USD', 'EUR', 'GBP', 'JPY'];

    // Fetch functions
    const fetchPortfolioData = useCallback(async () => {
        if (!token || !preferredCurrency) return;
        
        try {
            setLoading(true);
            const headers = await getAuthHeaders();
            
            // Fetch portfolio items
            const response = await fetch(`${config.apiUrl}/api/portfolio?currency=${preferredCurrency}`, {
                headers: headers
            });
            
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            
            const portfolioItems = await response.json();
            

            // Early return - don't fetch anything else if portfolio is empty
            if (portfolioItems.length === 0) {
                setPortfolio([]);
                return; 
            }
            
            // Fetch detailed stock data
            const detailedPortfolio = await Promise.all(
                portfolioItems.map(async (item) => {
                    try {
                        const detailResponse = await fetch(
                            `${config.apiUrl}/api/stocks/${item.company_id}?currency=${preferredCurrency}`,
                            { headers }
                        );
                        
                        if (!detailResponse.ok) return null;
                        
                        const detailData = await detailResponse.json();
                        if (!detailData.success) return null;
                        
                        return {
                            ...detailData.data,
                            quantity: item.quantity,
                            average_buy_price: item.average_buy_price,
                            original_buy_price: item.original_buy_price,
                            original_currency: item.original_currency,
                            purchase_date: item.purchase_date,
                            total_value: item.quantity * detailData.data.current_price,
                            growth: ((detailData.data.current_price - item.average_buy_price) / item.average_buy_price) * 100
                        };
                    } catch (err) {
                        console.error(`Error fetching stock details for ${item.company_id}:`, err);
                        return null;
                    }
                })
            );
            
            setPortfolio(detailedPortfolio.filter(item => item !== null));
            setIsOverviewExpanded(true);
        } catch (err) {
            console.error('Portfolio fetch error:', err);
        } finally {
            setLoading(false);
            setHasLoadedOnce(true);
        }
    }, [token, preferredCurrency, getAuthHeaders]);

    const fetchPerformanceData = useCallback(async (range) => {
          // Don't fetch performance data if portfolio is empty
        if (!token || !preferredCurrency || portfolio.length === 0) return;
        
        try {
            const headers = await getAuthHeaders();
            
            // Fetch performance data
            const performanceResponse = await fetch(
                `${config.apiUrl}/api/portfolio/performance?range=${range}&currency=${preferredCurrency}`,
                { headers: headers }
            );
            
            if (!performanceResponse.ok) throw new Error(`HTTP error! status: ${performanceResponse.status}`);
            
            const performanceResults = await performanceResponse.json();
            
            // Fetch dividend data
            const dividendResponse = await fetch(
                `${config.apiUrl}/api/portfolio/dividends?range=${range}&currency=${preferredCurrency}`,
                { headers: headers }
            );
            
            const dividendResults = dividendResponse.ok ? await dividendResponse.json() : { dividends: [] };
            
            // Update all performance data at once
            setPerformanceData({
                values: performanceResults.totalValue || [],
                growth: performanceResults.growth || [],
                dividends: dividendResults.dividends || [],
                summary: performanceResults.summary || {
                    averageGrowth: 0,
                    totalGrowth: 0,
                    periodGrowth: 0,
                    currency: preferredCurrency
                }
            });
        } catch (error) {
            console.error('Performance data fetch error:', error);
        }
    }, [token, preferredCurrency, getAuthHeaders, portfolio]);

    // Event handlers
    const handleCurrencyChange = useCallback(async (currency) => {
        if (currency === preferredCurrency) return;
        
        try {
            trackEvent(EVENTS.CURRENCY_CHANGE, { 
                newCurrency: currency,
                oldCurrency: preferredCurrency
            });

            setPreferredCurrency(currency);
            
            const headers = await getAuthHeaders();
            const response = await fetch(`${config.apiUrl}/api/portfolio/preferred-currency`, {
                method: 'POST',
                headers: {
                    ...headers,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ currency })
            });                
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
        } catch (error) {
            console.error('Error updating preferred currency:', error);
        }
    }, [getAuthHeaders, preferredCurrency]);

    const handleRangeChange = useCallback((range) => {
        if (range !== selectedRange) {
            setSelectedRange(range);
        }
    }, [selectedRange]);

    const handleAddToPortfolio = useCallback(async (stockData) => {
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${config.apiUrl}/api/portfolio/${stockData.id}`, {
                method: 'POST',
                headers: {
                    ...headers,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    quantity: Number(stockData.quantity),
                    purchase_date: stockData.purchaseDate,
                    trackTransaction: true
                })
            });
    
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
    
            trackEvent(EVENTS.ADD_TO_PORTFOLIO, {
                stockId: stockData.id,
                stockName: stockData.name,
                quantity: stockData.quantity,
                purchaseDate: stockData.purchaseDate
            });
            
            await fetchPortfolioData();
        } catch (error) {
            console.error('Failed to add to portfolio:', error);
        }
    }, [getAuthHeaders, fetchPortfolioData]);
    
    const removeFromPortfolio = useCallback(async (stockId) => {
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${config.apiUrl}/api/portfolio/${stockId}`, {
                method: 'DELETE',
                headers: headers
            });
    
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
    
            await fetchPortfolioData();
        } catch (error) {
            console.error('Failed to remove from portfolio:', error);
        }
    }, [getAuthHeaders, fetchPortfolioData]);

    // Effects
    useEffect(() => {
        if (!token) {
            navigate('/login');
        }
    }, [token, navigate]);

    useEffect(() => {
        const fetchPreferredCurrency = async () => {
            if (!token) return;
            
            try {
                const headers = await getAuthHeaders();
                const response = await fetch(`${config.apiUrl}/api/portfolio/preferred-currency`, { headers });
                if (!response.ok) return;
                const data = await response.json();
                if (data && data.currency) setPreferredCurrency(data.currency);
            } catch (error) {
                console.error('Error fetching preferred currency:', error);
            }
        };
        
        fetchPreferredCurrency();
    }, [token, getAuthHeaders]);

    useEffect(() => {
        if (token && preferredCurrency) {
            fetchPortfolioData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, preferredCurrency]);

    useEffect(() => {
        if (token && preferredCurrency) {
            fetchPerformanceData(selectedRange);
        }
    }, [selectedRange, token, preferredCurrency, fetchPerformanceData]);

    // This useEffect sets the overlay visibility
    useEffect(() => {
        let timeoutId;
        // Only decide whether to show the guide after loading is complete
        if (!loading && hasLoadedOnce) {
            // Add a small delay to prevent flickering during state transitions
            timeoutId = setTimeout(() => {
                setShowEmptyGuide(portfolio.length === 0);
            }, 300); // 300ms delay prevents visible flickering
        }
        
        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [portfolio.length, loading, hasLoadedOnce]);

    // Computed values
    const portfolioStats = useMemo(() => {
        const totalValue = portfolio.reduce((sum, stock) => sum + (stock.current_price * stock.quantity), 0);
        const totalCost = portfolio.reduce((sum, stock) => sum + (stock.average_buy_price * stock.quantity), 0);
        const totalGrowth = totalCost > 0 ? ((totalValue - totalCost) / totalCost) * 100 : 0;
        const totalDividends = portfolio.reduce((sum, stock) => sum + ((stock.dividend || 0) * stock.quantity), 0);
        const numberOfStocks = portfolio.length;
        
        const totalPurchasePrice = portfolio.reduce((sum, stock) => {
            const price = parseFloat(stock.average_buy_price) || 0;
            const quantity = parseFloat(stock.quantity) || 0;
            return sum + (price * quantity);
        }, 0);
        
        const averagePERatio = portfolio.length > 0 
            ? portfolio.reduce((sum, stock) => sum + (parseFloat(stock.trailing_p_e) || 0), 0) / portfolio.length 
            : 0;
        
        return {
            totalValue,
            totalCost,
            totalGrowth,
            totalDividends,
            totalDividendYield: totalValue > 0 ? (totalDividends / totalValue) * 100 : 0,
            numberOfStocks,
            totalPurchasePrice,
            averagePERatio,
            sectorDistribution: portfolio.reduce((acc, stock) => {
                acc[stock.sector] = (acc[stock.sector] || 0) + (stock.current_price * stock.quantity);
                return acc;
            }, {})
        };
    }, [portfolio]);

    const sortedPortfolio = useMemo(() => {
        const portfolioToSort = filteredStocks || portfolio;

        return sortStocks(portfolioToSort, sortBy, sortOrder);
    }, [portfolio, filteredStocks, sortBy, sortOrder]);

    const distributionData = useMemo(() => {
        if (!portfolio || portfolio.length === 0) return [];
      
        const viewGenerators = {
            stocks: () => portfolio.map(stock => ({
                name: stock.name,
                value: stock.total_value,
                id: stock.id
            })),
            sectors: () => {
                const sectorMap = portfolio.reduce((acc, stock) => {
                    const sector = stock.sector || 'Unknown';
                    acc[sector] = (acc[sector] || 0) + stock.total_value;
                    return acc;
                }, {});
                
                return Object.entries(sectorMap).map(([sector, value]) => ({
                    name: sector,
                    value: value
                }));
            },
            countries: () => {
                const countryMap = portfolio.reduce((acc, stock) => {
                    const country = stock.country || 'Unknown';
                    acc[country] = (acc[country] || 0) + stock.total_value;
                    return acc;
                }, {});
                
                return Object.entries(countryMap).map(([country, value]) => ({
                    name: country,
                    value: value
                }));
            },
            currencies: () => {
                const currencyMap = portfolio.reduce((acc, stock) => {
                    const currency = stock.trading_currency || 'Unknown';
                    acc[currency] = (acc[currency] || 0) + stock.total_value;
                    return acc;
                }, {});
                
                return Object.entries(currencyMap).map(([currency, value]) => ({
                    name: currency,
                    value: value
                }));
            },
            dividend: () => {
                const dividendCategories = {
                    'Hohe Dividende (>4%)': stock => stock.dividend_yield > 4,
                    'Medium Dividende (2-4%)': stock => stock.dividend_yield >= 2 && stock.dividend_yield <= 4,
                    'Niedrige Dividende (0-2%)': stock => stock.dividend_yield > 0 && stock.dividend_yield < 2,
                    'Keine Dividende': stock => !stock.dividend_yield || stock.dividend_yield === 0
                };
                
                const dividendMap = {};
                
                portfolio.forEach(stock => {
                    for (const [category, condition] of Object.entries(dividendCategories)) {
                        if (condition(stock)) {
                            dividendMap[category] = (dividendMap[category] || 0) + stock.total_value;
                            break;
                        }
                    }
                });
                
                return Object.entries(dividendMap).map(([category, value]) => ({
                    name: category,
                    value: value
                }));
            },
            performance: () => {
                const performanceCategories = {
                    'Top Performer (>20%)': stock => stock.growth > 20,
                    'Higher Performer (5-20%)': stock => stock.growth >= 5 && stock.growth <= 20,
                    'Neutral (-5% to 5%)': stock => stock.growth > -5 && stock.growth < 5,
                    'Low Performer (-20% to -5%)': stock => stock.growth >= -20 && stock.growth <= -5,
                    'Schlechte Performer (<-20%)': stock => stock.growth < -20
                };
                
                const performanceMap = {};
                
                portfolio.forEach(stock => {
                    for (const [category, condition] of Object.entries(performanceCategories)) {
                        if (condition(stock)) {
                            performanceMap[category] = (performanceMap[category] || 0) + stock.total_value;
                            break;
                        }
                    }
                });
                
                return Object.entries(performanceMap).map(([category, value]) => ({
                    name: category,
                    value: value
                }));
            },
            pe: () => {
                const peCategories = {
                    'Niedriges KGV (<10)': stock => stock.trailing_p_e < 10 && stock.trailing_p_e > 0,
                    'Mittleres KGV (10-20)': stock => stock.trailing_p_e >= 10 && stock.trailing_p_e <= 20,
                    'Hohes KGV (20-50)': stock => stock.trailing_p_e > 20 && stock.trailing_p_e <= 50,
                    'Sehr hohes KGV (>50)': stock => stock.trailing_p_e > 50,
                    'Unklar': stock => !stock.trailing_p_e || stock.trailing_p_e <= 0
                };
                
                const peMap = {};
                
                portfolio.forEach(stock => {
                    for (const [category, condition] of Object.entries(peCategories)) {
                        if (condition(stock)) {
                            peMap[category] = (peMap[category] || 0) + stock.total_value;
                            break;
                        }
                    }
                });
                
                return Object.entries(peMap).map(([category, value]) => ({
                    name: category,
                    value: value
                }));
            }
        };
        
        const generator = viewGenerators[activeDistributionView];
        return generator ? generator() : [];
    }, [portfolio, activeDistributionView]);

    // Component logic
    const getChartColor = () => {
        const summary = performanceData.summary;
        
        if (activeChartView === 'value') {
            return summary.periodGrowth >= 0 ? 'var(--success-color)' : 'var(--danger-color)';
        } else if (activeChartView === 'growth') {
            return summary.averageGrowth >= 0 ? 'var(--success-color)' : 'var(--danger-color)';
        } else {
            return 'var(--primary-color)';
        }
    };

    const getChartData = () => {
        switch (activeChartView) {
            case 'value':
                return performanceData.values;
            case 'growth':
                return performanceData.growth;
            case 'dividends':
                return performanceData.dividends;
            default:
                return [];
        }
    };

    const getChartValue = () => {
        const summary = performanceData.summary;
        
        switch (activeChartView) {
            case 'value':
                return formatCurrencyValue(portfolioStats.totalValue, preferredCurrency);
            case 'growth':
                return formatCurrencyValue(summary.totalGrowth, preferredCurrency);
            case 'dividends':
                return formatCurrencyValue(
                    performanceData.dividends.reduce((sum, item) => sum + item.value, 0), 
                    preferredCurrency
                );
            default:
                return 'N/A';
        }
    };

    const getChartGrowthValue = () => {
        const summary = performanceData.summary;
        
        if (activeChartView === 'dividends') {
            return 'Dividenden im letzten ' + (selectedRange === 'year' ? 'Jahr' : selectedRange === 'month' ? 'Monat' : 'Woche');
        }
        
        const growth = activeChartView === 'value' ? summary.periodGrowth : summary.averageGrowth;
        const sign = growth >= 0 ? '↑' : '↓';
        const period = activeChartView === 'growth' ? 
            (selectedRange === 'year' ? ' Ø pro Monat' : ' Ø pro Tag') : 
            '%';
        
        return `${sign} ${Math.abs(growth).toFixed(2)}${period}`;
    };

    const EmptyPortfolioGuide = ({ onClose }) => {
        const scrollToSearch = () => {
          const stockSection = document.querySelector('.stocks-section');
          if (stockSection) {
            stockSection.scrollIntoView({ behavior: 'smooth' });
            // Focus the search input after scrolling
            setTimeout(() => {
              const searchInput = document.querySelector('.stocks-section input[type="text"]');
              if (searchInput) searchInput.focus();
            }, 600);
          }
          onClose();
        };
      
        return (
          <div className="empty-portfolio-overlay" role="dialog" aria-modal="true" aria-label="Portfolio ist leer">
            <div className="empty-portfolio-card">
              <div className="empty-header">
                <i className="fas fa-chart-line fa-3x" aria-hidden="true"></i>
                <h2>Starte deine Aktienanalyse</h2>
              </div>
              <p>Dein Portfolio ist aktuell leer. Füge die ersten Aktien hinzu, um deine Investments zu verfolgen.</p>
              <div className="empty-portfolio-actions">
                <button type="button" className="app-btn app-btn--primary" onClick={scrollToSearch}>
                  <i className="fas fa-plus" aria-hidden="true"></i> Erste Aktie hinzufügen
                </button>
                <button type="button" className="app-btn app-btn--secondary" onClick={onClose}>
                  Später hinzufügen
                </button>
              </div>
            </div>
          </div>
        );
      };


    // Render component
    return (
        <div className="portfolio-container">
            {!loading && portfolio.length === 0 && showEmptyGuide && (
                <EmptyPortfolioGuide onClose={() => setShowEmptyGuide(false)} />
            )}
            {/* Portfolio Overview Section */}
            <section className="overview-section">
            <div className="section-card">
                <div className="section-header-with-controls">
                    <h2 className='section-title'>Portfolio Übersicht</h2>
                    <div className="currency-selector">
                        <span className="currency-label">Währung:</span>
                        <select 
                            value={preferredCurrency} 
                            onChange={(e) => handleCurrencyChange(e.target.value)}
                            className="modern-input currency-select"
                            aria-label="Währung"
                        >
                            {availableCurrencies.map(currency => (
                                <option key={currency} value={currency}>{currency}</option>
                            ))}
                        </select>
                    </div>
                </div>
                    
                    <div className={`portfolio-overview ${isOverviewExpanded ? 'block' : 'hidden'}`}>
                        <div className="chart-toggle mb-6" role="tablist" aria-label="Übersicht">
                            <button 
                                type="button"
                                role="tab"
                                aria-selected={activeOverviewTab === 'metrics'}
                                className={`toggle-button ${activeOverviewTab === 'metrics' ? 'active' : ''}`}
                                onClick={() => setActiveOverviewTab('metrics')}
                            >
                                Kennzahlen
                            </button>
                            <button 
                                type="button"
                                role="tab"
                                aria-selected={activeOverviewTab === 'distribution'}
                                className={`toggle-button ${activeOverviewTab === 'distribution' ? 'active' : ''}`}
                                onClick={() => setActiveOverviewTab('distribution')}
                            >
                                Verteilung
                            </button>
                        </div>

                        {activeOverviewTab === 'metrics' ? (
                            <MetricsCards 
                                portfolioStats={portfolioStats}
                                performanceData={performanceData}
                                preferredCurrency={preferredCurrency}
                                formatCurrencyValue={formatCurrencyValue}
                                formatNumber={formatNumber}
                            />
                            ) : (
                            <div className="distribution-container">
                                <div className="filter-scroll-container">
                                    <div className="filter-cards">
                                        {[
                                            { id: 'stocks', icon: 'fa-coins', label: 'Aktien' },
                                            { id: 'sectors', icon: 'fa-industry', label: 'Sektoren' },
                                            { id: 'countries', icon: 'fa-globe', label: 'Länder' },
                                            { id: 'currencies', icon: 'fa-money-bill-wave', label: 'Währungen' },
                                            { id: 'dividend', icon: 'fa-hand-holding-usd', label: 'Dividenden' },
                                            { id: 'performance', icon: 'fa-chart-bar', label: 'Performance' },
                                            { id: 'pe', icon: 'fa-balance-scale', label: 'KGV' }
                                        ].map(view => (
                                            <button 
                                                type="button"
                                                key={view.id}
                                                className={`filter-card ${activeDistributionView === view.id ? 'active' : ''}`}
                                                onClick={() => setActiveDistributionView(view.id)}
                                                aria-pressed={activeDistributionView === view.id}
                                            >
                                                <div className="filter-card-icon">
                                                    <i className={`fas ${view.icon}`} aria-hidden="true"></i>
                                                </div>
                                                <div className="filter-card-content">
                                                    <span className="filter-card-label">{view.label}</span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                
                                <div className="distribution-chart">
                                    <PortfolioPieChart 
                                        data={distributionData}
                                        title={activeDistributionView === 'stocks' ? 'Aktien' : 
                                               activeDistributionView === 'sectors' ? 'Sektoren' : 
                                               activeDistributionView === 'currencies' ? 'Währungen' : 
                                               activeDistributionView === 'countries' ? 'Länder' :
                                               activeDistributionView === 'dividend' ? 'Dividenden' :
                                               activeDistributionView === 'performance' ? 'Performance' :
                                               activeDistributionView === 'pe' ? 'KGV' :
                                               'Verteilung'}
                                        currencySymbol={CURRENCY_SYMBOLS[preferredCurrency] || preferredCurrency}
                                        currency={preferredCurrency}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Updates Section */}
            <section className="updates-section">
                <div className="section-card">
                    <h2 className='section-title'>Schnellcheck</h2>
                    <PortfolioUpdates 
                        portfolio={portfolio}
                        performanceData={performanceData}
                        portfolioStats={portfolioStats}
                        preferredCurrency={preferredCurrency}
                    />
                </div>
            </section>

            {/* Charts Section */}
            <section className="charts-section">
                <div className="section-card">
                    <h2 className='section-title'>Entwicklung</h2>
                    <ChartToggle 
                        activeView={activeChartView} 
                        onToggle={setActiveChartView} 
                    />
                    
                    <div className="timeframe-buttons">
                        {['week', 'month', 'year'].map(range => (
                            <button 
                                type="button"
                                key={range}
                                className={`timeframe-button ${selectedRange === range ? 'active' : ''}`}
                                onClick={() => handleRangeChange(range)}
                                aria-pressed={selectedRange === range}
                            >
                                {range === 'week' ? '1W' : range === 'month' ? '1M' : '1J'}
                            </button>
                        ))}
                    </div>
                    
                    <div className="chart-value">
                        <div className={`total-value ${
                            activeChartView === 'value' 
                                ? (performanceData.summary.periodGrowth >= 0 ? 'positive' : 'negative')
                                : activeChartView === 'growth'
                                ? (performanceData.summary.totalGrowth >= 0 ? 'positive' : 'negative')
                                : 'positive'
                        }`}>
                            {getChartValue()}
                        </div>
                        <div className={`growth-value ${
                            activeChartView === 'dividends' ? 'positive' : 
                            performanceData.summary.averageGrowth >= 0 ? 'positive' : 'negative'
                        }`}>
                            {getChartGrowthValue()}
                        </div>
                    </div>
                    
                    {activeChartView === 'value' ? (
                        <PerformanceChart 
                            data={getChartData()}
                            dataKey="value"
                            currency={preferredCurrency}
                            chartColor={getChartColor()}
                        />
                    ) : (
                        <PortfolioGrowthChart 
                            performanceData={getChartData()}
                            currency={preferredCurrency}
                            chartColor={getChartColor()}
                            selectedRange={selectedRange}
                        />
                    )}
                </div>
            </section>

            {/* Stocks Section */}
            <section className="stocks-section">
                <div className="section-card">
                    <PortfolioStockHeader
                        sortBy={sortBy}
                        onSortChange={(value) => setSortBy(value)}
                        sortOrder={sortOrder}
                        onSortOrderChange={setSortOrder}
                        onAddStock={handleAddToPortfolio}
                        portfolio={portfolio}
                        onFilterChange={setFilteredStocks}
                        getAuthHeaders={getAuthHeaders}
                        preferredCurrency={preferredCurrency}
                        initialStockId={addStockParam}
                        onInitialStockHandled={() => {
                            if (addStockParam) setSearchParams({}, { replace: true });
                        }}
                    />
                    
                    <div className="stock-grid">
                        {preferredCurrency && sortedPortfolio.length > 0 ? (
                            sortedPortfolio.map(stock => (
                                <StockCard
                                    key={stock.id}
                                    stock={stock}
                                    showQuantity={true}
                                    isPortfolio={true}
                                    onAction={removeFromPortfolio}
                                    actionIcon="fa-trash"
                                    actionType="remove"
                                    sortBy={sortBy}
                                    currency={preferredCurrency}
                                    initialExpanded={false}
                                />
                            ))
                        ) : (
                            <div className="no-stocks-message">
                                <i className="fas fa-info-circle"></i>
                                <p>Keine Aktien gefunden, die den ausgewählten Kriterien entsprechen.</p>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Portfolio;